import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { KVMarkup, RolodexEntryPatchJson } from 'types'

type UpdateMarkupParams<T extends KVMarkup> = {
  markup: T
  jsonFields: string | string[]
  setter: (a: T) => T
  v: unknown | unknown[]
  commandType:
    | KestrelCommands.UPDATE_ANNOTATION
    | KestrelCommands.UPDATE_MEASUREMENT
    | KestrelCommands.UPDATE_PLANE_INTERSECTION
    | KestrelCommands.UPDATE_SURFACE_AREA
    | KestrelCommands.UPDATE_INSPECTION_AREA
    | KestrelCommands.UPDATE_PRIMITIVE
  markupArray: T[] | undefined
  setMarkupArray: React.Dispatch<React.SetStateAction<T[] | undefined>>
  rolodexField?:
    | 'annotations'
    | 'polygons'
    | 'measurements'
    | 'planeIntersections'
    | 'inspectionAreas'
    | 'primitives'
  toJSON: (m: T) => Record<string, unknown>
  sendCommand: (json: string | object) => void
  selectedMarkup: KVMarkup | undefined
  setSelectedMarkup: React.Dispatch<React.SetStateAction<KVMarkup | undefined>>
  kestrelModelId: string
  rolodexPatch?: (patchData: RolodexEntryPatchJson[]) => Promise<unknown>
}

export function updateMarkup<T extends KVMarkup>({
  markup,
  jsonFields,
  setter,
  v,
  commandType,
  markupArray,
  setMarkupArray,
  rolodexField,
  toJSON,
  sendCommand,
  selectedMarkup,
  setSelectedMarkup,
  kestrelModelId,
  rolodexPatch,
}: UpdateMarkupParams<T>) {
  setter(markup)

  const getIdFieldName = (commandType: string): string => {
    const baseName = commandType.replace('update', '')
    return baseName.charAt(0).toLowerCase() + baseName.slice(1) + 'Id'
  }

  const command: Record<string, unknown> = {
    command: commandType,
    [getIdFieldName(commandType)]: '' + markup.id,
  }
  if (Array.isArray(jsonFields) && Array.isArray(v)) {
    for (let i = 0; i < jsonFields.length; i++) {
      command[jsonFields[i]] = v[i]
    }
  } else if (!Array.isArray(jsonFields) && !Array.isArray(v)) {
    command[jsonFields] = v
  } else {
    throw new Error(
      'updateMarkup:jsonFields and v must both be arrays or both be non-arrays'
    )
  }

  if (sendCommand) sendCommand(command)
  const updatedMarkups = markupArray?.map((m: T) =>
    m.id === markup.id ? setter(m) : m
  )

  setMarkupArray(updatedMarkups)
  if (
    !!selectedMarkup &&
    selectedMarkup instanceof markup.constructor &&
    selectedMarkup.id === markup.id
  ) {
    setSelectedMarkup(setter(markup))
  }

  if (rolodexPatch && rolodexField) {
    // Update the markup in rolodex
    const patchData: RolodexEntryPatchJson = {
      id: kestrelModelId,
      data: {
        [rolodexField]: updatedMarkups?.map((m) => toJSON(m)) ?? [],
      },
    }
    rolodexPatch([patchData])
  }
}
